// src/constants/job-next-action.ts
//
// What a recruiter should do about a job, in one phrase (#3569).
//
// The Applicants column reported INVENTORY — how many candidates exist — while
// the thing a recruiter acts on is FLOW: what is new, what is waiting on them,
// what has gone cold. `Days Active` measures the age of the posting, not
// whether candidates are going stale, so a 56-day job that is fully screened
// and a 56-day job with ten unread resumes looked identical.
//
// The ladder is evaluated top to bottom and STOPS at the first match, so every
// row carries exactly one state — its most important one. "Up to date" is a
// real rung, not a fallback for tidiness: an explicit "you're fine" is what
// makes the red and amber ones worth reacting to. If everything were coloured,
// nothing would be.

import type { Job } from "@type/job";

/**
 * Tunable, and deliberately in one place.
 *
 * These are guesses until real usage data exists — the point of holding them
 * here is that tuning them is a one-line change rather than a hunt through a
 * table component.
 */
export const NEXT_ACTION_THRESHOLDS = {
    /** Below this age, a job with no applicants is simply new, not failing. */
    noApplicantsGraceDays: 7,
    /** No stage change in this long and a candidate has gone cold. */
    stalledDays: 21,
} as const;

export type NextActionTone = "critical" | "warning" | "success";

export interface NextAction {
    /** Stable id, for sorting and tests — not shown. */
    id: "no_applicants" | "to_review" | "stalled" | "awaiting_interview" | "up_to_date";
    label: string;
    tone: NextActionTone;
    /** Sort weight: lower is more urgent. */
    severity: number;
}

/** Whole days between a timestamp and now. Negative clock skew reads as 0. */
export function daysSince(iso: string | null | undefined): number {
    if (!iso) return 0;
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return 0;
    return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

/**
 * The single most important thing about this job right now.
 *
 * Two rungs — `stalled` and `awaiting_interview` — depend on fields the jobs
 * list does not send yet (see ourteam-backend#4654). They are wired here
 * rather than left out so that landing the backend fields is a data change
 * with no component change: today the optional fields are undefined, the rungs
 * never match, and the ladder falls through to the ones that are real.
 *
 * Approximating them from what IS available was the other option and is the
 * wrong one — a "stalled" chip derived from the posting's age would be a
 * confident claim about candidates nobody measured.
 */
export function nextActionFor(job: Job): NextAction {
    const total = job.applicants_count ?? 0;
    const age = daysSince(job.created_at);

    // 1. Nothing is arriving, and it has been long enough that this is a
    //    finding rather than a new posting. The most urgent row on the page and
    //    the one that used to render as blank space.
    if (total === 0 && age >= NEXT_ACTION_THRESHOLDS.noApplicantsGraceDays) {
        return { id: "no_applicants", label: `No applicants in ${age}d`, tone: "critical", severity: 0 };
    }

    // 2. Waiting on the recruiter. `total_review` is the backend's own
    //    "Pending Review" count — the same number the third icon carried.
    const toReview = job.total_review ?? 0;
    if (toReview >= 1) {
        return { id: "to_review", label: `${toReview} to review`, tone: "warning", severity: 1 };
    }

    // 3. Waiting on nobody — candidates going cold. Needs `total_stalled`.
    const stalled = job.total_stalled;
    if (typeof stalled === "number" && stalled >= 1) {
        return {
            id: "stalled",
            label: `${stalled} stalled ${NEXT_ACTION_THRESHOLDS.stalledDays}d+`,
            tone: "warning",
            severity: 2,
        };
    }

    // 4. Screened, not yet scheduled. Needs `total_awaiting_interview`.
    const awaiting = job.total_awaiting_interview;
    if (typeof awaiting === "number" && awaiting >= 1) {
        return { id: "awaiting_interview", label: `${awaiting} awaiting interview`, tone: "warning", severity: 3 };
    }

    return { id: "up_to_date", label: "Up to date", tone: "success", severity: 4 };
}

/** Semantic tokens, not one-off hex — the chip has to age with the design system. */
export const NEXT_ACTION_TONE_CLASS: Record<NextActionTone, string> = {
    critical: "border-rose-200 bg-rose-50 text-rose-700",
    warning: "border-amber-200 bg-amber-50 text-amber-800",
    success: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

/**
 * The per-stage detail the icon cluster used to carry, for the count's tooltip.
 *
 * A stage with nothing in it is omitted rather than shown as zero: the tooltip
 * exists to say what the number is made of, and "Uploaded 0" is not a
 * component of anything. The count itself still renders 0 — that is a
 * different statement, and the one this ticket exists to stop hiding.
 */
export function applicantBreakdown(job: Job): string {
    const parts: string[] = [];
    if ((job.total_applied ?? 0) > 0) parts.push(`${job.total_applied} applied`);
    if ((job.total_upload ?? 0) > 0) parts.push(`${job.total_upload} uploaded`);
    if ((job.total_review ?? 0) > 0) parts.push(`${job.total_review} pending review`);
    return parts.length > 0 ? parts.join(" · ") : "No applicants yet";
}
